// ============================================
// pages/Salon/SalonProducts.js
// Salon Products Management — Full CRUD + S3 image upload
// ============================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../common/context/AuthContext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SALON_API_URL } from '../../api/config';
import {
  Plus, X, Edit2, Trash2, Search, Package,
  Loader2, MoreVertical, Image as ImageIcon,
  Hash, CheckCircle, XCircle, Upload, ChevronDown,
  AlertTriangle,
} from 'lucide-react';

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORIES = ['Skin', 'Hair', 'Nails', 'Makeup', 'Waxing', 'Tools', 'Other'];

const CAT_COLORS = {
  Skin:   'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  Hair:   'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  Nails:  'bg-pink-50 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
  Makeup: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  Waxing: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  Tools:  'bg-slate-50 text-slate-700 dark:bg-slate-900/30 dark:text-slate-300',
  Other:  'bg-gray-50 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300',
};

const EMPTY_FORM = {
  name:         '',
  price:        '',
  stock_qty:    '',
  category:     '',
  product_code: '',
  description:  '',
  image_url:    '',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function stockBadge(qty) {
  if (qty > 10) return { label: 'In Stock',     cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',  icon: CheckCircle };
  if (qty > 0)  return { label: 'Low Stock',    cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300', icon: AlertTriangle };
  return             { label: 'Out of Stock', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',     icon: XCircle };
}

// ─── Image Uploader component ─────────────────────────────────────────────────

function ImageUploader({ value, onChange, token }) {
  const inputRef     = useRef();
  const [preview,    setPreview]    = useState(value || '');
  const [uploading,  setUploading]  = useState(false);
  const [dragOver,   setDragOver]   = useState(false);

  useEffect(() => { setPreview(value || ''); }, [value]);

  const handleFile = async (file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPG, PNG, WEBP images allowed'); return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB'); return;
    }

    // Show local preview immediately
    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);
    setUploading(true);

    try {
      // 1. Get pre-signed URL from backend
      const res  = await fetch(`${SALON_API_URL}/products/upload-url`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ fileType: file.type, fileName: file.name }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to get upload URL');

      // 2. PUT file directly to S3
      const s3Res = await fetch(data.data.uploadUrl, {
        method:  'PUT',
        headers: { 'Content-Type': file.type },
        body:    file,
      });
      if (!s3Res.ok) throw new Error('S3 upload failed');

      setPreview(data.data.publicUrl);
      onChange(data.data.publicUrl);
      toast.success('Image uploaded ✓');
    } catch (err) {
      toast.error(err.message || 'Image upload failed');
      setPreview(value || '');
      onChange(value || '');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
        Product Image <span className="font-normal normal-case text-gray-400">(optional)</span>
      </label>

      {preview ? (
        <div className="relative w-full h-44 rounded-2xl overflow-hidden border border-gray-200 dark:border-slate-700 group">
          <img src={preview} alt='../../pages/Product' className="w-full h-full object-cover" />
          {uploading && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <Loader2 size={24} className="animate-spin text-white" />
            </div>
          )}
          {!uploading && (
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100 gap-3">
              <button
                onClick={() => inputRef.current?.click()}
                className="bg-white text-gray-800 rounded-xl px-3 py-1.5 text-xs font-semibold hover:bg-gray-100 flex items-center gap-1.5"
              >
                <Upload size={12} /> Change
              </button>
              <button
                onClick={() => { setPreview(''); onChange(''); }}
                className="bg-red-500 text-white rounded-xl px-3 py-1.5 text-xs font-semibold hover:bg-red-600 flex items-center gap-1.5"
              >
                <X size={12} /> Remove
              </button>
            </div>
          )}
        </div>
      ) : (
        <div
          onClick={() => !uploading && inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`w-full h-36 rounded-2xl border-2 border-dashed flex flex-col items-center justify-center gap-2 cursor-pointer transition-all ${
            dragOver
              ? 'border-purple-400 bg-purple-50 dark:bg-purple-900/20'
              : 'border-gray-200 dark:border-slate-700 hover:border-purple-300 dark:hover:border-purple-600 bg-gray-50 dark:bg-slate-800'
          }`}
        >
          {uploading ? (
            <Loader2 size={24} className="animate-spin text-purple-500" />
          ) : (
            <>
              <ImageIcon size={28} className="text-gray-300 dark:text-slate-600" />
              <p className="text-xs text-gray-400 dark:text-slate-500 text-center px-4">
                Click or drag & drop<br />
                <span className="text-gray-300">JPG, PNG, WEBP · max 5MB</span>
              </p>
            </>
          )}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        className="hidden"
        onChange={(e) => handleFile(e.target.files[0])}
      />
    </div>
  );
}

// ─── Product Card ──────────────────────────────────────────────────────────────

function ProductCard({ product, onEdit, onDelete }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const stock  = stockBadge(product.stock_qty);
  const StockIcon = stock.icon;
  const catColor = CAT_COLORS[product.category] || CAT_COLORS.Other;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden flex flex-col">

      {/* Image */}
      <div className="relative h-40 bg-gray-50 dark:bg-slate-900 flex-shrink-0">
        {product.image_url ? (
          <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package size={36} className="text-gray-200 dark:text-slate-700" />
          </div>
        )}
        {/* Category badge */}
        <span className={`absolute top-2 left-2 text-xs font-bold px-2 py-0.5 rounded-full ${catColor}`}>
          {product.category}
        </span>
        {/* Menu */}
        <div className="absolute top-2 right-2">
          <button
            onClick={() => setMenuOpen(o => !o)}
            className="p-1.5 bg-white dark:bg-slate-800 rounded-lg shadow text-gray-500 hover:text-gray-700 transition-colors"
          >
            <MoreVertical size={14} />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-8 z-20 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-100 dark:border-slate-700 py-1 w-36">
                <button onClick={() => { onEdit(product); setMenuOpen(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700">
                  <Edit2 size={13} /> Edit
                </button>
                <button onClick={() => { onDelete(product); setMenuOpen(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">
                  <Trash2 size={13} /> Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-4 flex flex-col gap-2 flex-1">
        <div>
          <h3 className="font-bold text-gray-900 dark:text-white text-sm leading-tight line-clamp-2">
            {product.name}
          </h3>
          {product.product_code && (
            <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
              <Hash size={10} /> {product.product_code}
            </p>
          )}
        </div>

        {/* Stock badge */}
        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full w-fit ${stock.cls}`}>
          <StockIcon size={10} />
          {stock.label} {product.stock_qty > 0 ? `(${product.stock_qty})` : ''}
        </span>

        {/* Price + qty row */}
        <div className="flex items-center justify-between mt-auto pt-2 border-t border-gray-50 dark:border-slate-700">
          <span className="font-black text-purple-600 dark:text-purple-400 text-base">₹{product.price}</span>
          <span className="text-xs text-gray-400 dark:text-slate-500">Qty: {product.stock_qty}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Add/Edit Modal ───────────────────────────────────────────────────────────

function ProductModal({ mode, product, onClose, onSave, token }) {
  const [form,   setForm]   = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (mode === 'edit' && product) {
      setForm({
        name:         product.name         || '',
        price:        String(product.price || ''),
        stock_qty:    String(product.stock_qty ?? ''),
        category:     product.category     || '',
        product_code: product.product_code || '',
        description:  product.description  || '',
        image_url:    product.image_url    || '',
      });
    }
  }, [mode, product]);

  const set = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    setErrors(e => ({ ...e, [key]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim())                                              e.name     = 'Product name is required';
    if (!form.price || isNaN(form.price) || Number(form.price) < 0)    e.price    = 'Valid price is required';
    if (!form.category)                                                 e.category = 'Category is required';
    if (form.stock_qty !== '' && isNaN(form.stock_qty))                 e.stock_qty = 'Must be a number';
    if (form.product_code && !/^[a-zA-Z0-9\-_]+$/.test(form.product_code))
      e.product_code = 'Code can only contain letters, numbers, - and _';
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      await onSave({
        name:         form.name.trim(),
        price:        Number(form.price),
        stock_qty:    form.stock_qty !== '' ? Number(form.stock_qty) : 0,
        category:     form.category,
        product_code: form.product_code.trim() || null,
        image_url:    form.image_url || null,
        description:  form.description.trim(),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-slate-800 flex-shrink-0">
          <div>
            <h2 className="text-lg font-black text-gray-900 dark:text-white">
              {mode === 'add' ? '📦 Add New Product' : '✏️ Edit Product'}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">Fill in the product details below</p>
          </div>
          <button onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400 hover:text-gray-600 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">

          {/* Image */}
          <ImageUploader
            value={form.image_url}
            onChange={url => set('image_url', url)}
            token={token}
          />

          {/* Name */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
              Product Name <span className="text-red-500">*</span>
            </label>
            <input
              className={`w-full bg-gray-50 dark:bg-slate-800 border rounded-xl px-4 py-2.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.name ? 'border-red-400' : 'border-gray-200 dark:border-slate-700'}`}
              placeholder="e.g. Vitamin C Serum"
              value={form.name}
              onChange={e => set('name', e.target.value)}
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          {/* Category */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">
              Category <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map(cat => (
                <button key={cat} onClick={() => set('category', cat)}
                  className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold border transition-all ${
                    form.category === cat
                      ? 'bg-purple-600 text-white border-purple-600'
                      : 'bg-gray-50 dark:bg-slate-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-slate-700 hover:border-purple-300'
                  }`}
                >{cat}</button>
              ))}
            </div>
            {errors.category && <p className="text-red-500 text-xs mt-1">{errors.category}</p>}
          </div>

          {/* Price + Stock in a row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                Price <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 font-semibold text-sm">₹</span>
                <input
                  type="number" min="0" step="0.01"
                  className={`w-full bg-gray-50 dark:bg-slate-800 border rounded-xl pl-8 pr-4 py-2.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.price ? 'border-red-400' : 'border-gray-200 dark:border-slate-700'}`}
                  placeholder="0.00"
                  value={form.price}
                  onChange={e => set('price', e.target.value)}
                />
              </div>
              {errors.price && <p className="text-red-500 text-xs mt-1">{errors.price}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                No. of Products
              </label>
              <input
                type="number" min="0"
                className={`w-full bg-gray-50 dark:bg-slate-800 border rounded-xl px-4 py-2.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.stock_qty ? 'border-red-400' : 'border-gray-200 dark:border-slate-700'}`}
                placeholder="0"
                value={form.stock_qty}
                onChange={e => set('stock_qty', e.target.value)}
              />
              {errors.stock_qty && <p className="text-red-500 text-xs mt-1">{errors.stock_qty}</p>}
            </div>
          </div>

          {/* Stock status (visual indicator — derived) */}
          {form.stock_qty !== '' && (
            <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold ${stockBadge(Number(form.stock_qty)).cls}`}>
              {React.createElement(stockBadge(Number(form.stock_qty)).icon, { size: 13 })}
              {stockBadge(Number(form.stock_qty)).label}
              {Number(form.stock_qty) === 0 && <span className="font-normal ml-1 opacity-70">— this product is Out of Stock</span>}
              {Number(form.stock_qty) > 0 && Number(form.stock_qty) <= 10 && <span className="font-normal ml-1 opacity-70">— running low!</span>}
            </div>
          )}

          {/* Product Code */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
              Product Code <span className="font-normal normal-case text-gray-400">(optional)</span>
            </label>
            <div className="relative">
              <Hash size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                className={`w-full bg-gray-50 dark:bg-slate-800 border rounded-xl pl-9 pr-4 py-2.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.product_code ? 'border-red-400' : 'border-gray-200 dark:border-slate-700'}`}
                placeholder="e.g. SKN-001 or VC2024"
                value={form.product_code}
                onChange={e => set('product_code', e.target.value.toUpperCase())}
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">Letters, numbers, hyphens and underscores only</p>
            {errors.product_code && <p className="text-red-500 text-xs mt-1">{errors.product_code}</p>}
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
              Description <span className="font-normal normal-case text-gray-400">(optional)</span>
            </label>
            <textarea
              rows={3}
              className="w-full bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              placeholder="Describe the product..."
              value={form.description}
              onChange={e => set('description', e.target.value)}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 dark:border-slate-800 flex gap-3 flex-shrink-0">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-sm font-semibold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Package size={15} />}
            {saving ? 'Saving...' : mode === 'add' ? 'Add Product' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Delete Modal ─────────────────────────────────────────────────────────────

function DeleteModal({ product, onClose, onConfirm, loading }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-sm p-6 flex flex-col gap-4">
        <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-2xl flex items-center justify-center">
          <Trash2 size={22} className="text-red-500" />
        </div>
        <div>
          <h3 className="font-black text-gray-900 dark:text-white text-base">Delete Product?</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Permanently delete <strong className="text-gray-700 dark:text-gray-300">"{product.name}"</strong>? This also removes the product image. Cannot be undone.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-sm font-semibold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-800">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading}
            className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-bold transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SalonProducts() {
  const { userToken: token } = useAuth();

  const [products,       setProducts]       = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [searchQuery,    setSearchQuery]    = useState('');
  const [filterCat,      setFilterCat]      = useState('All');
  const [filterStock,    setFilterStock]    = useState('all'); // all | in | out
  const [modal,          setModal]          = useState(null); // {type, product?}
  const [deleting,       setDeleting]       = useState(false);

  const headers = {
    Authorization:  `Bearer ${token}`,
    'Content-Type': 'application/json',
    Accept:         'application/json',
  };

  // ── Fetch ─────────────────────────────────────────────────────────────────

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCat !== 'All') params.set('category', filterCat);
      if (filterStock === 'in')  params.set('inStock', 'true');
      if (filterStock === 'out') params.set('inStock', 'false');

      const res  = await fetch(`${SALON_API_URL}/products?${params}`, { headers });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to fetch');
      setProducts(data.data);
    } catch (err) {
      toast.error(err.message || 'Could not load products');
    } finally {
      setLoading(false);
    }
  }, [token, filterCat, filterStock]); // eslint-disable-line

  useEffect(() => { if (token) fetchProducts(); }, [fetchProducts, token]);

  // ── Create ────────────────────────────────────────────────────────────────

  const handleCreate = async (payload) => {
    const res  = await fetch(`${SALON_API_URL}/products`, { method: 'POST', headers, body: JSON.stringify(payload) });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.message || 'Failed to create');
    toast.success('Product added! 📦');
    setModal(null);
    fetchProducts();
  };

  // ── Update ────────────────────────────────────────────────────────────────

  const handleUpdate = async (payload) => {
    const res  = await fetch(`${SALON_API_URL}/products/${modal.product.id}`, { method: 'PUT', headers, body: JSON.stringify(payload) });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.message || 'Failed to update');
    toast.success('Product updated! ✅');
    setModal(null);
    fetchProducts();
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res  = await fetch(`${SALON_API_URL}/products/${modal.product.id}?permanent=true`, { method: 'DELETE', headers });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to delete');
      toast.success('Product deleted');
      setModal(null);
      fetchProducts();
    } catch (err) {
      toast.error(err.message || 'Could not delete');
    } finally {
      setDeleting(false);
    }
  };

  // ── Derived state ─────────────────────────────────────────────────────────

  const filtered = products.filter(p => {
    const q = searchQuery.toLowerCase();
    return (
      p.name?.toLowerCase().includes(q) ||
      p.product_code?.toLowerCase().includes(q) ||
      p.category?.toLowerCase().includes(q)
    );
  });

  const grouped = filtered.reduce((acc, p) => {
    if (!acc[p.category]) acc[p.category] = [];
    acc[p.category].push(p);
    return acc;
  }, {});

  const totalProducts  = products.length;
  const inStockCount   = products.filter(p => p.stock_qty > 0).length;
  const outStockCount  = products.filter(p => p.stock_qty === 0).length;
  const lowStockCount  = products.filter(p => p.stock_qty > 0 && p.stock_qty <= 10).length;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="p-6 min-h-full">
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
        <div className="flex-1">
          <h1 className="text-2xl font-black text-gray-900 dark:text-white flex items-center gap-2">
            <Package size={22} className="text-purple-600" />
            Products
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage salon retail products · {totalProducts} total
          </p>
        </div>
        <button
          onClick={() => setModal({ type: 'add' })}
          className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-lg shadow-purple-200 dark:shadow-purple-900/30 transition-all hover:scale-105"
        >
          <Plus size={16} /> Add Product
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total Products', value: totalProducts, color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-50 dark:bg-purple-900/20' },
          { label: 'In Stock',       value: inStockCount,  color: 'text-green-600 dark:text-green-400',   bg: 'bg-green-50 dark:bg-green-900/20'   },
          { label: 'Out of Stock',   value: outStockCount, color: 'text-red-600 dark:text-red-400',       bg: 'bg-red-50 dark:bg-red-900/20'       },
          { label: 'Low Stock',      value: lowStockCount, color: 'text-yellow-600 dark:text-yellow-400', bg: 'bg-yellow-50 dark:bg-yellow-900/20' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-2xl p-4 text-center`}>
            <p className={`text-2xl font-black ${s.color}`}>{s.value}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-4 mb-6 flex flex-wrap gap-3">

        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="Search by name, code, category..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Category filter */}
        <div className="relative">
          <select
            value={filterCat}
            onChange={e => setFilterCat(e.target.value)}
            className="appearance-none pl-3 pr-8 py-2 rounded-xl text-xs font-semibold bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
          >
            <option value="All">All Categories</option>
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
          <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>

        {/* Stock filter */}
        <div className="flex gap-1.5">
          {[
            { val: 'all', label: 'All Stock' },
            { val: 'in',  label: '✓ In Stock' },
            { val: 'out', label: '✗ Out of Stock' },
          ].map(opt => (
            <button key={opt.val} onClick={() => setFilterStock(opt.val)}
              className={`px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
                filterStock === opt.val
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-50 dark:bg-slate-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-600'
              }`}
            >{opt.label}</button>
          ))}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <Loader2 size={28} className="animate-spin text-purple-600" />
          <p className="text-sm text-gray-400">Loading products...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 gap-4">
          <div className="w-16 h-16 bg-purple-50 dark:bg-purple-900/20 rounded-2xl flex items-center justify-center">
            <Package size={28} className="text-purple-400" />
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">
            {searchQuery ? 'No products match your search' : 'No products yet'}
          </p>
          {!searchQuery && (
            <button
              onClick={() => setModal({ type: 'add' })}
              className="text-purple-600 dark:text-purple-400 text-sm font-semibold flex items-center gap-1 hover:underline"
            >
              <Plus size={14} /> Add your first product
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <div className="flex items-center gap-2 mb-3">
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${CAT_COLORS[cat] || CAT_COLORS.Other}`}>{cat}</span>
                <span className="text-xs bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded-full">{items.length}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {items.map(p => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    onEdit={prod => setModal({ type: 'edit', product: prod })}
                    onDelete={prod => setModal({ type: 'delete', product: prod })}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {modal?.type === 'add' && (
        <ProductModal mode="add" token={token} onClose={() => setModal(null)} onSave={handleCreate} />
      )}
      {modal?.type === 'edit' && (
        <ProductModal mode="edit" product={modal.product} token={token} onClose={() => setModal(null)} onSave={handleUpdate} />
      )}
      {modal?.type === 'delete' && (
        <DeleteModal product={modal.product} onClose={() => setModal(null)} onConfirm={handleDelete} loading={deleting} />
      )}
    </div>
  );
}